// src/pages/Home.jsx - Reimagined with Freepik-inspired design
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Zap, 
  Target, 
  TrendingUp,
  BookOpen,
  Users,
  Trophy,
  ArrowRight,
  Play,
  Star,
  CheckCircle,
  Globe,
  BarChart
} from 'lucide-react';
import { bookAccessService } from '../services/bookAccessService';

export const Home = () => {
  const navigate = useNavigate();
  const [publicBooks, setPublicBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  
  // Refs for intersection observer
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const statsRef = useRef(null);
  const booksRef = useRef(null);
  
  const [heroVisible, setHeroVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [booksVisible, setBooksVisible] = useState(false);

  useEffect(() => {
    loadPublicBooks();
    
    // Scroll event listener for parallax
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    
    // Intersection observer for animations
    const observerOptions = { threshold: 0.1, rootMargin: '0px 0px -50px 0px' };
    
    const heroObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setHeroVisible(true);
    }, observerOptions);
    
    const featuresObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setFeaturesVisible(true);
    }, observerOptions);
    
    const statsObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setStatsVisible(true);
    }, observerOptions);
    
    const booksObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setBooksVisible(true);
    }, observerOptions);
    
    if (heroRef.current) heroObserver.observe(heroRef.current);
    if (featuresRef.current) featuresObserver.observe(featuresRef.current);
    if (statsRef.current) statsObserver.observe(statsRef.current);
    if (booksRef.current) booksObserver.observe(booksRef.current);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      heroObserver.disconnect();
      featuresObserver.disconnect();
      statsObserver.disconnect();
      booksObserver.disconnect();
    };
  }, []);

  const loadPublicBooks = async () => {
    try {
      const books = await bookAccessService.getPublicBooks();
      setPublicBooks(books.slice(0, 6));
    } catch (error) {
      console.error('Error loading public books:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Sparkles,
      title: 'Interactive Learning',
      description: 'Engage with dynamic content that adapts to your reading pace and comprehension',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: Target,
      title: 'Track Progress',
      description: 'Monitor your journey with detailed analytics and personalized insights',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Zap,
      title: 'Instant Feedback',
      description: 'Get real-time responses to quizzes and interactive elements',
      gradient: 'from-orange-500 to-red-500'
    },
    {
      icon: TrendingUp,
      title: 'Gamified Experience',
      description: 'Earn achievements and unlock content as you progress',
      gradient: 'from-green-500 to-emerald-500'
    }
  ];

  const stats = [
    { value: '10M+', label: 'Pages Read', icon: BookOpen },
    { value: '50K+', label: 'Active Readers', icon: Users },
    { value: '98%', label: 'Satisfaction', icon: Trophy }
  ];

  const showcaseItems = [
    { title: 'Completions', value: '2.5M+', icon: CheckCircle },
    { title: 'Active Users', value: '50K+', icon: Users },
    { title: 'Achievements', value: '1M+', icon: Trophy },
    { title: 'Global Reach', value: '120+', icon: Globe }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section with Parallax */}
      <section 
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8"
        style={{ 
          transform: `translateY(${scrollY * 0.5}px)`,
          opacity: Math.max(1 - (scrollY / 800), 0)
        }}
      >
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50" 
             style={{ animation: 'gradient-xy 15s ease infinite' }} />
        
        {/* Floating Orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-20" 
             style={{ animation: 'blob 7s infinite' }} />
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl opacity-20" 
             style={{ animation: 'blob 7s infinite 2s' }} />
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-xl opacity-20" 
             style={{ animation: 'blob 7s infinite 4s' }} />

        <div className={`relative z-10 max-w-6xl mx-auto text-center transition-all duration-1000 ${
          heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {/* Floating Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg mb-8"
               style={{ animation: 'fade-in 0.5s ease-out' }}>
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Reading, reimagined
            </span>
          </div>

          {/* Main Headline with Gradient */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-slate-900 via-purple-900 to-slate-900 bg-clip-text text-transparent"
                  style={{ backgroundSize: '200% auto', animation: 'gradient-x 15s ease infinite' }}>
              Interactive Digital
            </span>
            <br />
            <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 bg-clip-text text-transparent"
                  style={{ backgroundSize: '200% auto', animation: 'gradient-x 15s ease infinite' }}>
              Books Platform
            </span>
          </h1>

          <p className="text-xl sm:text-2xl text-slate-600 mb-12 max-w-3xl mx-auto">
            Create, share, and experience engaging interactive content. 
            Import from CSV or build from scratch with our modern platform.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/signup"
              className="group px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <button 
              onClick={() => document.getElementById('books-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="group px-8 py-4 bg-white text-slate-700 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
            >
              <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
              Explore Books
            </button>
          </div>
        </div>
      </section>

      {/* Scrolling Stats Strip */}
      <section className="py-12 bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 overflow-hidden">
        <div className="flex gap-12 items-center whitespace-nowrap" 
             style={{ animation: 'scroll-x 30s linear infinite' }}>
          {[...showcaseItems, ...showcaseItems, ...showcaseItems].map((item, index) => (
            <div key={index} className="flex items-center gap-4 px-6">
              <item.icon className="w-8 h-8 text-white" />
              <div>
                <div className="text-3xl font-bold text-white">{item.value}</div>
                <div className="text-white/80">{item.title}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features Grid */}
      <section ref={featuresRef} className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-1000 delay-200 ${
            featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
              Features you'll love
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Everything you need for an immersive, engaging reading experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${
                  featuresVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${(index + 1) * 100}ms` }}
              >
                {/* Gradient Border Effect */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-r ${feature.gradient} p-3 mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                  <feature.icon className="w-full h-full text-white" />
                </div>

                <h3 className="text-2xl font-bold mb-3 text-slate-900">
                  {feature.title}
                </h3>
                
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>

                <div className="mt-6 flex items-center text-purple-600 font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                  Learn more
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Public Books Section */}
      <section ref={booksRef} id="books-section" className="py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-1000 ${
            booksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-slate-900">
              Explore Public Books
            </h2>
            <p className="text-xl text-slate-600">
              Discover engaging interactive content from our community
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : publicBooks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {publicBooks.map((book, index) => (
                <div
                  key={book.id}
                  className={`group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${
                    booksVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${(index + 1) * 100}ms` }}
                >
                  {book.coverImage && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={book.coverImage}
                        alt={book.title}
                        className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center gap-2 text-white text-sm">
                          <BookOpen className="w-4 h-4" />
                          <span>{book.contentCount || 0} items</span>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-purple-600 transition-colors">
                      {book.title}
                    </h3>
                    <p className="text-slate-600 mb-4 line-clamp-2">
                      {book.description || 'No description available'}
                    </p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div className="flex items-center gap-2 text-sm text-slate-500">
                        <Users className="w-4 h-4" />
                        <span>{book.viewCount || 0} views</span>
                      </div>
                      
                      <Link
                        to={`/book/${book.id}`}
                        className="text-purple-600 hover:text-purple-700 font-semibold flex items-center gap-1 group"
                      >
                        View Book
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No public books available yet</p>
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-slate-800 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <div className="absolute top-20 left-10 w-96 h-96 bg-purple-500 rounded-full filter blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl" />
        </div>

        <div className={`relative z-10 max-w-7xl mx-auto transition-all duration-1000 ${
          statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-white">
              Join thousands of readers
            </h2>
            <p className="text-xl text-slate-300">
              Be part of the interactive reading revolution
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`text-center transition-all duration-1000 ${
                  statsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${(index + 1) * 150}ms` }}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full mb-4">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-slate-400 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 rounded-3xl p-12 shadow-2xl transform hover:scale-105 transition-transform duration-300">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to transform your reading?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Start your interactive journey today
            </p>
            <Link
              to="/signup"
              className="inline-block px-8 py-4 bg-white text-purple-600 rounded-xl font-bold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              Get Started Now
            </Link>
          </div>
        </div>
      </section>

      {/* Add inline styles for animations */}
      <style>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes gradient-xy {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes scroll-x {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        
        @keyframes fade-in {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};